import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FiInbox, FiCreditCard, FiShoppingCart, FiRepeat, FiFileText } from 'react-icons/fi';
import AppLayout from '../../components/layout/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import DataTable from '../../components/ui/DataTable';
import StatusPill from '../../components/ui/StatusPill';
import { Card, CardBody } from '../../components/ui/Card';
import EmptyState from '../../components/ui/EmptyState';
import ErrorBanner from '../../components/ui/ErrorBanner';
import { Select } from '../../components/ui/Input';
import SearchInput from '../../components/ui/SearchInput';
import DateDisplay from '../../components/shared/DateDisplay';
import WorkflowActions from '../../components/ui/WorkflowActions';
import WorkflowTimeline from '../../components/ui/WorkflowTimeline';
import { listPendingApprovals, listSubmittedWorkflows } from '../../api/workflows';
import Drawer from '../../components/ui/Drawer';
import { useAuth } from '../../auth/AuthContext';

const ENTITY_ICONS = {
  EXPENSE: FiCreditCard,
  PROCUREMENT: FiShoppingCart,
  REIMBURSEMENT: FiRepeat,
  INVOICE: FiFileText,
};

const ENTITY_TONES = {
  EXPENSE: 'bg-info/10 text-info',
  PROCUREMENT: 'bg-warning/10 text-warning',
  REIMBURSEMENT: 'bg-accent/10 text-accent-dark',
  INVOICE: 'bg-primary/10 text-primary',
};

function EntityChip({ type }) {
  const Icon = ENTITY_ICONS[type] || FiInbox;
  const tone = ENTITY_TONES[type] || 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${tone}`}>
      <Icon className="text-xs" /> {type}
    </span>
  );
}

const QUERY_KEY_BY_ENTITY = {
  EXPENSE: ['expenses'],
  PROCUREMENT: ['procurement'],
  REIMBURSEMENT: ['reimbursements'],
};

export default function ApprovalsInbox() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState(isAdmin ? 'submitted' : 'pending');
  const [entityFilter, setEntityFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  const pending = useQuery({
    queryKey: ['approvals', 'pending'],
    queryFn: () => listPendingApprovals(),
  });
  const submitted = useQuery({
    queryKey: ['approvals', 'submitted'],
    queryFn: () => listSubmittedWorkflows(),
    enabled: tab === 'submitted' && isAdmin,
  });

  const active = tab === 'pending' ? pending : submitted;
  const rows = useMemo(() => {
    let list = active.data || [];
    if (entityFilter) {
      list = list.filter((w) => w.entityType === entityFilter);
    }
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter((w) =>
        (w.entityType && w.entityType.toLowerCase().includes(term)) ||
        (w.entityId && String(w.entityId).toLowerCase().includes(term)) ||
        (w.workflowStatus && w.workflowStatus.toLowerCase().includes(term))
      );
    }
    return list;
  }, [active.data, entityFilter, search]);

  const entityTypes = useMemo(() => {
    const set = new Set();
    (active.data || []).forEach((w) => w.entityType && set.add(w.entityType));
    return Array.from(set).sort();
  }, [active.data]);

  const columns = [
    {
      key: 'entityType',
      header: 'Type',
      render: (r) => <EntityChip type={r.entityType} />,
    },
    {
      key: 'entityId',
      header: 'Reference',
      render: (r) => <span className="font-mono text-xs">{r.entityType}#{r.entityId}</span>,
    },
    {
      key: 'workflowStatus',
      header: 'Status',
      render: (r) => <StatusPill value={r.workflowStatus} />,
    },
    {
      key: 'currentStep',
      header: 'Current Step',
      render: (r) => (
        <span className="text-xs text-ink-muted">
          Step {r.currentStep ?? '—'} • {r.currentApproverUsername || (r.currentApproverId ? `Approver #${r.currentApproverId}` : '—')}
        </span>
      ),
    },
    {
      key: 'submittedAt',
      header: 'Submitted',
      render: (r) => <DateDisplay value={r.submittedAt || r.createdAt} withTime />,
    },
  ];

  return (
    <AppLayout title="Approvals Inbox" breadcrumb="Workflow / Approvals">
      <PageHeader
        title="Approvals inbox"
        description="Review pending workflow items and act on requests assigned to you."
      />

      <div className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-3">
        <Card>
          <CardBody>
            <div className="text-xs uppercase tracking-wider text-ink-muted">Pending action</div>
            <div className="mt-1 text-2xl font-semibold text-ink">
              {pending.isLoading ? '…' : pending.data?.length || 0}
            </div>
          </CardBody>
        </Card>
        {isAdmin && (
          <Card>
            <CardBody>
              <div className="text-xs uppercase tracking-wider text-ink-muted">Requests (dept)</div>
              <div className="mt-1 text-2xl font-semibold text-ink">
                {submitted.isFetching ? '…' : submitted.data?.length || 0}
              </div>
            </CardBody>
          </Card>
        )}
        <Card>
          <CardBody>
            <div className="text-xs uppercase tracking-wider text-ink-muted">By entity type</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {entityTypes.length === 0 ? (
                <span className="text-xs text-ink-faint">—</span>
              ) : (
                entityTypes.map((t) => <EntityChip key={t} type={t} />)
              )}
            </div>
          </CardBody>
        </Card>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 shadow-card mr-auto">

          {isAdmin && (
            <button
              type="button"
              onClick={() => setTab('submitted')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === 'submitted' ? 'bg-primary text-white' : 'text-ink-muted hover:bg-slate-100'
                }`}
            >
              Requests
            </button>

          )}
          <button
            type="button"
            onClick={() => setTab('pending')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium ${tab === 'pending' ? 'bg-primary text-white' : 'text-ink-muted hover:bg-slate-100'
              }`}
          >
            My pending
          </button>
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search ref or status..."
          className="w-full sm:w-64"
        />
        <Select
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
          className="w-full sm:w-48"
        >
          <option value="">All entity types</option>
          <option value="EXPENSE">Expenses</option>
          <option value="REIMBURSEMENT">Reimbursements</option>
          <option value="PROCUREMENT">Procurement</option>
        </Select>
      </div>

      {active.error && (
        <div className="mb-4">
          <ErrorBanner error={active.error} title="Failed to load approvals" />
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        loading={active.isLoading}
        keyField="id"
        onRowClick={(row) => setSelected(row)}
        empty={
          <EmptyState
            icon={FiInbox}
            title={tab === 'pending' ? 'No pending approvals' : 'No submitted workflows'}
            description={
              tab === 'pending'
                ? 'When workflow steps land on you, they will appear here.'
                : 'Submitted requests across your department show up here.'
            }
          />
        }
      />

      <Drawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={
          selected
            ? `${selected.entityType} #${selected.entityId}`
            : 'Workflow'
        }
        width="lg"
        footer={
          selected && (
            <WorkflowActions
              workflow={selected}
              invalidateKeys={[
                ['approvals', 'pending'],
                ['approvals', 'submitted'],
                ...(QUERY_KEY_BY_ENTITY[selected.entityType] || []).map((k) => [k]),
              ]}
              onActed={() => setSelected(null)}
              showFor="always"
            />
          )
        }
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 rounded-md bg-slate-50 p-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted">Entity</div>
                <div className="font-medium text-ink">
                  {selected.entityType} #{selected.entityId}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted">Submitted by</div>
                <div className="font-medium text-ink">{selected.submittedByUsername || (selected.submittedBy ? `User #${selected.submittedBy}` : '—')}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted">Submitted at</div>
                <div className="font-medium text-ink">
                  <DateDisplay value={selected.submittedAt || selected.createdAt} withTime />
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted">Status</div>
                <div><StatusPill value={selected.workflowStatus} /></div>
              </div>
            </div>
            <WorkflowTimeline workflow={selected} />
          </div>
        )}
      </Drawer>
    </AppLayout>
  );
}
